/* eslint-disable @typescript-eslint/no-unused-vars */
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { Box, Chip, Grid, Typography, styled, useTheme } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'

import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import { IUserListFilter } from '../../_models/newBookingInterface'
import AutoSuggestion from '../../components/common/autoSuggestion'
import { clearUserList, fetchUsersList, newBookingData } from '../../slices/newBookingSlice'
import { useTranslation } from 'react-i18next'

type FormInputs = {
  emailForOthers: string
}
interface IFormFields {
  isExpanded: boolean
  userEmails: string[]
  handleAddUserEmailButton: (email: string) => void
  handleEmailClearClick: (email: string) => void
}
const StyledChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.secondary.main,
  backgroundColor: theme.palette.label.main,
  borderRadius: '0.5rem',
  padding: '1.2rem 1rem 1.2rem 1rem',
  fontSize: '0.875rem',
  fontFamily: 'Lexend',

  '.MuiChip-deleteIcon	': {
    marginLeft: '.75rem',
    fill: `${theme.palette.primary.main} !important`,
  },
}))
const BookForOthersFormFields = (props: IFormFields) => {
  const { isExpanded, userEmails, handleAddUserEmailButton, handleEmailClearClick } = props
  const dispatch = useAppDispatch()
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<FormInputs>()

  const { t } = useTranslation()
  const { userListDetails, userFilter, isUsersListLoading, completeUserList } =
    useAppSelector(newBookingData)

  const [clearInput, setClearInput] = useState(false)
  const [emailSearchTerm, setEmailSearchTerm] = useState<string>('')
  const theme = useTheme()

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setValue('emailForOthers', '')
      setEmailSearchTerm('')
      setClearInput(true)
    }
  }

  const handleInputChange = (value: string) => {
    if (value !== emailSearchTerm && value.trim().length >= 3) {
      const updatedUserFilter: IUserListFilter = {
        ...userFilter, // Preserve existing filters like pagination
        email: value, // Update the email field with the search term
      }
      setEmailSearchTerm(value)
      dispatch(fetchUsersList(updatedUserFilter)) // Dispatch the API call with filters
    }
    dispatch(clearUserList())
  }

  const handleSelect = (value: { label?: string; subLabel: string } | null) => {
    if (value?.subLabel) {
      handleAddUserEmailButton(value?.subLabel)
      setValue('emailForOthers', '')
      setEmailSearchTerm('')
      setClearInput(true)
    }
  }

  const handleScroll: React.UIEventHandler<HTMLUListElement> = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget

    if (scrollTop + clientHeight >= scrollHeight - 10 && !isUsersListLoading) {
      // Trigger API call to fetch the next page
      const nextPage = userListDetails.current_page + 1

      if (nextPage <= userListDetails.last_page) {
        const updatedUserFilter: IUserListFilter = {
          ...userFilter,
          email: emailSearchTerm,
          page: nextPage, // Increment the page for pagination
        }

        dispatch(fetchUsersList(updatedUserFilter))
      }
    }
  }

  return (
    <form>
      <Grid
        container
        columnSpacing={'1.25rem'}
        width={isExpanded ? { xs: '100%', md: '50%' } : '100%'}
        mt={3}
      >
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6} mt={2}>
          <Controller
            name='emailForOthers'
            control={control}
            defaultValue=''
            rules={{
              maxLength: {
                value: 64,
                message: t('newBooking.emailMaxLengthError'),
              },
              pattern: {
                value: /^[a-zA-Z0-9._+]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,
                message: t('newBooking.emailValid'),
              },
            }}
            render={({ field }) => (
              <Box>
                <AutoSuggestion
                  options={completeUserList.map((user) => ({
                    label: user.name,
                    subLabel: user.email,
                  }))}
                  onSelect={(value) => {
                    handleSelect(value) // Handle selected value
                    field.onChange(value?.subLabel || '') // Update the field value
                  }}
                  onInputChange={handleInputChange}
                  isLoading={isUsersListLoading}
                  placeholder={t('newBooking.emailLabel')}
                  displaySubLabel={true}
                  startAdornment={<MailOutlineIcon />}
                  onKeyDown={handleKeyPress}
                  clearInput={clearInput}
                  onClear={() => setClearInput(false)}
                  handleScroll={handleScroll}
                  style={{ maxHeight: 200, overflow: 'auto' }}
                />
                {errors.emailForOthers && (
                  <Typography
                    variant='body2'
                    color='error'
                    sx={{ marginTop: '0.25rem', fontSize: '0.75rem' }}
                  >
                    {errors.emailForOthers.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>
      </Grid>
      <Box display='flex' mt={1}>
        <Box
          display='flex'
          flexWrap='wrap'
          justifyContent='flex-start'
          alignItems='flex-start'
          gap={2}
          mt={1}
        >
          {' '}
          {userEmails.map((data) => (
            <StyledChip
              deleteIcon={
                <CloseIcon style={{ fill: theme.palette.common.white, width: 20, height: 20 }} />
              }
              key={data}
              label={
                <Box display='flex'>
                  <Typography variant='subtitle2'>{data}</Typography>
                </Box>
              }
              onDelete={() => handleEmailClearClick(data)}
            />
          ))}{' '}
        </Box>
      </Box>
    </form>
  )
}

export default BookForOthersFormFields
